import React, { useCallback, useRef, useState } from 'react';
import type { FooterVideoData } from './types';

interface BackgroundVideoProps {
    /** Video data from CMS */
    videoData: FooterVideoData;
    /** Additional CSS class names */
    className?: string;
    /** CSS class for the cover image fallback */
    coverClassName?: string;
}

/**
 * Background video component for footer sections.
 * Prefers WebM format with MP4 fallback.
 * Shows cover image while loading or on error.
 */
export const BackgroundVideo: React.FC<BackgroundVideoProps> = ({
    videoData,
    className = '',
    coverClassName = '',
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const hasVideo = videoData.videoWebM || videoData.videoMp4;
    const showCover = !hasVideo || !isLoaded || hasError;

    const handleLoadedData = useCallback(() => {
        setIsLoaded(true);
    }, []);

    const handleError = useCallback(() => {
        setHasError(true);
    }, []);

    // Attempt to play video (may fail due to autoplay policies)
    const handleCanPlay = useCallback(() => {
        if (videoData.autoplay && videoRef.current) {
            videoRef.current.play().catch(() => {
                // Autoplay blocked by browser, show cover image
                setHasError(true);
            });
        }
    }, [videoData.autoplay]);

    return (
        <>
            {/* Cover image - shown while loading or on error */}
            {videoData.coverImage && showCover && (
                <div
                    className={`absolute inset-0 bg-cover bg-center bg-no-repeat ${coverClassName}`}
                    style={{ backgroundImage: `url(${videoData.coverImage})` }}
                    aria-hidden="true"
                />
            )}

            {/* Video element */}
            {hasVideo && !hasError && (
                <video
                    ref={videoRef}
                    className={`absolute inset-0 h-full w-full object-cover ${className}`}
                    autoPlay={videoData.autoplay}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    controls={false}
                    aria-hidden="true"
                    onLoadedData={handleLoadedData}
                    onError={handleError}
                    onCanPlay={handleCanPlay}
                    poster={videoData.coverImage || undefined}
                >
                    {/* WebM source (preferred) */}
                    {videoData.videoWebM && (
                        <source src={videoData.videoWebM} type="video/webm" />
                    )}
                    {/* MP4 source (fallback) */}
                    {videoData.videoMp4 && (
                        <source src={videoData.videoMp4} type="video/mp4" />
                    )}
                </video>
            )}
        </>
    );
};
